/** Host OS — Internal ChatGPT. A ChatGPT-style chat that answers from the
 *  business Knowledge Base (POST /host/kb/query). */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Send, Loader2, ArrowLeft, BookOpen, User as UserIcon, Sparkles, Check, X, AlertTriangle, Plus, MessageSquare, Trash2, History, Paperclip, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import HostLayout from "@/components/host/HostLayout";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

const SUGGESTIONS = [
  "How many classes do we have today?",
  "What's our revenue this month?",
  "Add a new lead: Jane Doe, jane@example.com",
  "Tag customer John as VIP",
];

// Loads an auth-gated attachment image as a blob object URL (an <img src> can't send auth headers).
const OpsImage = ({ attachmentId, previewUrl, alt, className }) => {
  const [url, setUrl] = useState(previewUrl || null);
  useEffect(() => {
    if (previewUrl) { setUrl(previewUrl); return; }
    let revoke;
    (async () => {
      try {
        const res = await fetch(`${API}/host/ops/attachments/${attachmentId}`, { headers: await authHeaders() });
        if (!res.ok) return;
        const blob = await res.blob();
        revoke = URL.createObjectURL(blob);
        setUrl(revoke);
      } catch { /* ignore */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [attachmentId, previewUrl]);
  if (!url) return <div className={`bg-muted animate-pulse ${className}`} />;
  return <img src={url} alt={alt} className={className} />;
};

const MessageAttachments = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-1.5" data-testid="message-attachments">
      {items.map((a) => (
        a.kind === "image" ? (
          <OpsImage
            key={a.attachment_id}
            attachmentId={a.attachment_id}
            previewUrl={a.previewUrl}
            alt={a.name}
            className="w-24 h-24 rounded-lg object-cover border border-border"
          />
        ) : (
          <span key={a.attachment_id} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-background border border-border text-xs max-w-[180px]" title={a.name}>
            <FileText className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{a.name}</span>
          </span>
        )
      ))}
    </div>
  );
};

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const HostAssistantChatPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', content, sources?}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [hasData] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false); // mobile drawer
  const [loadingConvo, setLoadingConvo] = useState(false);
  const [staged, setStaged] = useState([]); // {tempId, name, kind, status, attachment_id, previewUrl}
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/host/ops/conversations`, { headers: await authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setConversations(data.conversations || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const newChat = () => {
    setMessages([]);
    setActiveId(null);
    setInput("");
    setStaged([]);
    setHistoryOpen(false);
  };

  const kindOf = (file) => (file.type || "").startsWith("image/") ? "image" : "document";

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 5 - staged.length);
    for (const file of files) {
      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const kind = kindOf(file);
      const previewUrl = kind === "image" ? URL.createObjectURL(file) : null;
      setStaged((prev) => [...prev, { tempId, name: file.name, kind, status: "uploading", previewUrl }]);
      try {
        const form = new FormData();
        form.append("file", file);
        const res = await fetch(`${API}/host/ops/upload`, { method: "POST", headers: await authHeaders(), body: form });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || "Upload failed");
        setStaged((prev) => prev.map((s) => s.tempId === tempId
          ? { ...s, status: "ready", attachment_id: data.attachment_id, kind: data.kind || kind } : s));
      } catch (e) {
        setStaged((prev) => prev.map((s) => s.tempId === tempId ? { ...s, status: "error", error: e.message } : s));
      }
    }
  };

  const onPickFiles = (e) => { uploadFiles(e.target.files); e.target.value = ""; };
  const removeStaged = (tempId) => setStaged((prev) => prev.filter((s) => s.tempId !== tempId));

  const openConversation = async (id) => {
    if (id === activeId && messages.length) { setHistoryOpen(false); return; }
    setLoadingConvo(true);
    setHistoryOpen(false);
    setStaged([]);
    try {
      const res = await fetch(`${API}/host/ops/conversations/${id}`, { headers: await authHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMessages((data.messages || []).map((m) => ({
        role: m.role, content: m.content, sources: m.sources || [], attachments: m.attachments || [],
      })));
      setActiveId(id);
    } catch { /* ignore */ } finally { setLoadingConvo(false); }
  };

  const deleteConversation = async (e, id) => {
    e.stopPropagation();
    setConversations((prev) => prev.filter((c) => c.conversation_id !== id));
    if (id === activeId) newChat();
    try {
      await fetch(`${API}/host/ops/conversations/${id}`, { method: "DELETE", headers: await authHeaders() });
    } catch { /* ignore */ }
  };

  const uploading = staged.some((s) => s.status === "uploading");

  const send = async (text) => {
    const q = (text ?? input).trim();
    const readyAtts = staged.filter((s) => s.status === "ready");
    if ((!q && readyAtts.length === 0) || sending || uploading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    const msgAtts = readyAtts.map((s) => ({ attachment_id: s.attachment_id, name: s.name, kind: s.kind, previewUrl: s.previewUrl }));
    const next = [...messages, { role: "user", content: q, attachments: msgAtts }];
    setMessages(next);
    setInput("");
    setStaged([]);
    setSending(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const res = await fetch(`${API}/host/ops/chat`, {
        method: "POST", headers,
        body: JSON.stringify({ message: q, history, conversation_id: activeId, attachments: readyAtts.map((s) => s.attachment_id) }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.conversation_id) setActiveId(data.conversation_id);
      setMessages([...next, {
        role: "assistant",
        content: data.answer || "I couldn't find an answer.",
        sources: data.sources || [],
        pending: (data.pending_actions || []).map((p) => ({ ...p, state: "pending" })),
      }]);
      loadConversations();
    } catch {
      setMessages([...next, { role: "assistant", content: "Something went wrong reaching the assistant. Please try again.", sources: [] }]);
    } finally { setSending(false); }
  };

  const resolveAction = async (msgIndex, token, decision) => {
    // decision: 'confirm' | 'cancel'
    setMessages((prev) => prev.map((m, i) => i !== msgIndex ? m : {
      ...m, pending: (m.pending || []).map((p) => p.token === token ? { ...p, state: "loading" } : p),
    }));
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const res = await fetch(`${API}/host/ops/${decision}`, {
        method: "POST", headers, body: JSON.stringify({ token }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed");
      setMessages((prev) => {
        const updated = prev.map((m, i) => i !== msgIndex ? m : {
          ...m, pending: (m.pending || []).map((p) => p.token === token
            ? { ...p, state: decision === "confirm" ? "confirmed" : "cancelled" } : p),
        });
        const note = decision === "confirm"
          ? (data.message || "✅ Done.")
          : "Cancelled — no changes made.";
        return [...updated, { role: "assistant", content: note, sources: [], system: true }];
      });
    } catch (e) {
      setMessages((prev) => prev.map((m, i) => i !== msgIndex ? m : {
        ...m, pending: (m.pending || []).map((p) => p.token === token
          ? { ...p, state: "pending", error: e.message || "Something went wrong" } : p),
      }));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const RailContent = (
    <div className="flex flex-col h-full">
      <Button onClick={newChat} className="gap-2 rounded-xl w-full justify-start" data-testid="assistant-new-chat">
        <Plus className="w-4 h-4" /> New chat
      </Button>
      <div className="mt-4 flex-1 overflow-y-auto -mx-1 px-1" data-testid="assistant-conversation-list">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground px-2 mb-1.5">Recent</p>
        {conversations.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-3">No past chats yet.</p>
        ) : (
          <div className="space-y-0.5">
            {conversations.map((c) => (
              <button
                key={c.conversation_id}
                onClick={() => openConversation(c.conversation_id)}
                className={`group w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${
                  c.conversation_id === activeId ? "bg-muted text-foreground" : "hover:bg-muted/50 text-muted-foreground"
                }`}
                data-testid={`conversation-item-${c.conversation_id}`}
                title={c.title}
              >
                <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 min-w-0 truncate">{c.title}</span>
                <span className="text-[10px] text-muted-foreground/70 shrink-0 group-hover:hidden">{relTime(c.updated_at)}</span>
                <span
                  onClick={(e) => deleteConversation(e, c.conversation_id)}
                  className="hidden group-hover:flex shrink-0 text-muted-foreground hover:text-red-500 p-0.5 rounded"
                  data-testid={`conversation-delete-${c.conversation_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <HostLayout fluid>
      <div className="flex h-[calc(100vh-7rem)] gap-4" data-testid="host-assistant-chat-page">
        {/* History rail (desktop) */}
        <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border pr-4" data-testid="assistant-history-rail">
          {RailContent}
        </aside>

        {/* History drawer (mobile) */}
        {historyOpen && (
          <div className="md:hidden fixed inset-0 z-40" data-testid="assistant-history-drawer">
            <div className="absolute inset-0 bg-black/40" onClick={() => setHistoryOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[80%] bg-background border-r border-border p-4 shadow-xl">
              <div className="flex items-center justify-between mb-3">
                <span className="font-display text-sm">Chat history</span>
                <button onClick={() => setHistoryOpen(false)} className="text-muted-foreground" data-testid="assistant-history-close"><X className="w-4 h-4" /></button>
              </div>
              {RailContent}
            </div>
          </div>
        )}

        {/* Chat column */}
        <div className="flex flex-col flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-border">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setHistoryOpen(true)} className="md:hidden text-muted-foreground hover:text-foreground shrink-0" data-testid="assistant-history-toggle">
                <History className="w-5 h-5" />
              </button>
              <button onClick={() => navigate("/host/ai")} className="hidden md:block text-muted-foreground hover:text-foreground shrink-0" data-testid="assistant-back">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0">
                <Bot className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h1 className="font-display text-lg leading-tight truncate">Internal ChatGPT</h1>
                <p className="text-xs text-muted-foreground truncate">Runs your admin — schedule, customers, revenue & more</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/host/knowledge")} className="gap-1.5 shrink-0" data-testid="assistant-manage-kb">
              <BookOpen className="w-4 h-4" /> <span className="hidden sm:inline">Manage knowledge</span>
            </Button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto py-6" data-testid="assistant-messages">
            <div className="w-full max-w-3xl mx-auto space-y-6">
            {loadingConvo && (
              <div className="flex justify-center pt-10" data-testid="assistant-convo-loading">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loadingConvo && messages.length === 0 && (
              <div className="max-w-lg mx-auto text-center pt-6" data-testid="assistant-empty">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 text-emerald-700 dark:text-emerald-300 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7" />
                </div>
                <h2 className="font-display text-2xl">Ask me anything about your business</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Ask about today's classes, this week's schedule, revenue, or look up a customer — I check your live data and answer.
                </p>
                {hasData === false ? (
                  <Button className="mt-4" onClick={() => navigate("/host/knowledge")} data-testid="assistant-add-knowledge">
                    <BookOpen className="w-4 h-4 mr-2" /> Add knowledge
                  </Button>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-2 mt-5 text-left">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="rounded-xl border border-border p-3 text-sm hover:border-primary/50 hover:bg-muted/40 transition-colors"
                        data-testid={`assistant-suggestion-${s.slice(0, 12)}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!loadingConvo && messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}-${i}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-4 h-4" />
                  </div>
                )}
                <div className={`max-w-[80%] ${m.role === "user" ? "order-1" : ""}`}>
                  <MessageAttachments items={m.attachments} />
                  {m.content ? (
                    <div className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                      {m.content}
                    </div>
                  ) : null}
                  {m.role === "assistant" && (m.sources || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`chat-sources-${i}`}>
                      {m.sources.slice(0, 5).map((s, j) => {
                        const label = s.source_label || s.title || s.source_type || "Source";
                        const chip = (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-background border border-border text-muted-foreground">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {label}
                          </span>
                        );
                        return s.page_url ? (
                          <a key={j} href={s.page_url} target="_blank" rel="noreferrer" className="hover:opacity-80" title={s.page_url}>{chip}</a>
                        ) : <span key={j} title={s.snippet || ""}>{chip}</span>;
                      })}
                    </div>
                  )}
                  {m.role === "assistant" && (m.pending || []).length > 0 && (
                    <div className="mt-3 space-y-2" data-testid={`chat-actions-${i}`}>
                      {m.pending.map((p) => {
                        const destructive = p.kind === "delete_customer" || p.kind === "cancel_session";
                        return (
                          <div
                            key={p.token}
                            className={`rounded-xl border p-3 text-sm ${destructive ? "border-red-500/40 bg-red-500/5" : "border-primary/40 bg-primary/5"}`}
                            data-testid={`action-card-${p.token}`}
                          >
                            <div className="flex items-start gap-2">
                              {destructive
                                ? <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                : <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />}
                              <div className="min-w-0">
                                <p className="font-medium leading-tight">{p.title}</p>
                                <p className="text-muted-foreground text-[13px] mt-0.5">{p.summary}</p>
                              </div>
                            </div>
                            {p.error && <p className="text-xs text-red-500 mt-2">{p.error}</p>}
                            {p.state === "pending" && (
                              <div className="flex gap-2 mt-3">
                                <Button
                                  size="sm"
                                  variant={destructive ? "destructive" : "default"}
                                  onClick={() => resolveAction(i, p.token, "confirm")}
                                  className="gap-1.5 h-8"
                                  data-testid={`action-confirm-${p.token}`}
                                >
                                  <Check className="w-3.5 h-3.5" /> Confirm
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => resolveAction(i, p.token, "cancel")} className="gap-1.5 h-8" data-testid={`action-cancel-${p.token}`}>
                                  <X className="w-3.5 h-3.5" /> Cancel
                                </Button>
                              </div>
                            )}
                            {p.state === "loading" && (
                              <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Working…
                              </div>
                            )}
                            {p.state === "confirmed" && (
                              <div className="flex items-center gap-1.5 mt-3 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`action-done-${p.token}`}>
                                <Check className="w-3.5 h-3.5" /> Confirmed
                              </div>
                            )}
                            {p.state === "cancelled" && (
                              <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground" data-testid={`action-cancelled-${p.token}`}>
                                <X className="w-3.5 h-3.5" /> Cancelled
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary-ink flex items-center justify-center shrink-0 mt-0.5 order-2">
                    <UserIcon className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}

            {sending && (
              <div className="flex gap-3 justify-start" data-testid="assistant-typing">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="rounded-2xl px-4 py-3 bg-muted flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            </div>
          </div>

          {/* Composer */}
          <div className="border-t border-border pt-3">
            <div className="w-full max-w-3xl mx-auto">
            {staged.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="staged-attachments">
                {staged.map((s) => (
                  <div key={s.tempId} className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted border border-border text-xs max-w-[200px]" data-testid={`staged-${s.tempId}`}>
                    {s.kind === "image" && s.previewUrl
                      ? <img src={s.previewUrl} alt={s.name} className="w-6 h-6 rounded object-cover shrink-0" />
                      : <FileText className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />}
                    <span className="truncate">{s.name}</span>
                    {s.status === "uploading" && <Loader2 className="w-3 h-3 animate-spin shrink-0" />}
                    {s.status === "error" && <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" title={s.error} />}
                    <button onClick={() => removeStaged(s.tempId)} className="shrink-0 text-muted-foreground hover:text-foreground" data-testid={`staged-remove-${s.tempId}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2 rounded-2xl border border-border bg-card p-2 focus-within:border-primary/50 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.docx,.txt,.md,.csv"
                onChange={onPickFiles}
                className="hidden"
                data-testid="assistant-file-input"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || staged.length >= 5}
                className="rounded-xl shrink-0 text-muted-foreground hover:text-foreground"
                title="Attach files (images, PDF, DOCX, TXT, CSV)"
                data-testid="assistant-attach"
              >
                <Paperclip className="w-4 h-4" />
              </Button>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={hasData === false ? "Add knowledge first to start chatting…" : "Message your assistant…"}
                disabled={hasData === false || sending}
                className="border-0 focus-visible:ring-0 resize-none max-h-40 shadow-none bg-transparent"
                data-testid="assistant-input"
              />
              <Button onClick={() => send()} disabled={(!input.trim() && staged.filter((s) => s.status === "ready").length === 0) || sending || uploading || hasData === false} size="icon" className="rounded-xl shrink-0" data-testid="assistant-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground text-center mt-2">Attach images or documents (PDF, DOCX, TXT, CSV) — I'll read them. You'll always confirm before anything is saved.</p>
            </div>
          </div>
        </div>
      </div>
    </HostLayout>
  );
};

export default HostAssistantChatPage;
